import type {
  OrganizationSubscription,
  WorkspaceTrialStatus,
} from '@scrambl/shared'
import { TrialPhase } from '@scrambl/shared'

/**
 * The trial the activation dialog should activate: the server's trial state
 * when the rollout is on, otherwise one derived from an admin-granted
 * cardless trial (`requiresPaymentSetup`), so every "add a card" button
 * opens the same dialog. Null when there is nothing to activate.
 */
export function activatableTrial(
  subscription:
    | Pick<
        OrganizationSubscription,
        | 'trial'
        | 'requiresPaymentSetup'
        | 'paymentSetupCanPreserveTrial'
        | 'trialEnd'
        | 'plan'
      >
    | null
    | undefined,
  now = new Date(),
): WorkspaceTrialStatus | null {
  if (!subscription) return null
  if (subscription.trial) return subscription.trial
  if (!subscription.requiresPaymentSetup || !subscription.plan) return null
  const trialEnd = subscription.trialEnd
    ? new Date(subscription.trialEnd)
    : null
  const isOver = Boolean(trialEnd && trialEnd.getTime() <= now.getTime())
  return {
    phase: isOver ? TrialPhase.GRACE : TrialPhase.TRIALING,
    planKey: subscription.plan.key,
    planName: subscription.plan.displayName,
    trialEndsAt: (trialEnd ?? now).toISOString(),
    graceEndsAt: null,
    daysRemaining: trialEnd
      ? Math.max(
          0,
          Math.ceil((trialEnd.getTime() - now.getTime()) / 86_400_000),
        )
      : 0,
    isReadOnly: false,
    canPreserveTrial: Boolean(subscription.paymentSetupCanPreserveTrial),
    activationContacts: [],
  }
}

/**
 * Words and tone for the no-card trial chip and banner
 * (docs/PLAN-no-card-trials.md). Pure so the states are tested without
 * rendering. US English.
 */

export type TrialTone = 'quiet' | 'warning' | 'destructive'

/** Quiet normally, warning at ≤3 days and in grace, destructive when locked. */
export function trialTone(trial: WorkspaceTrialStatus): TrialTone {
  if (trial.phase === TrialPhase.LOCKED) return 'destructive'
  if (trial.phase === TrialPhase.GRACE) return 'warning'
  return trial.daysRemaining <= 3 ? 'warning' : 'quiet'
}

function days(count: number): string {
  return count === 1 ? '1 day' : `${count} days`
}

export function trialChipLabel(
  trial: WorkspaceTrialStatus,
  options: { isAdmin: boolean },
): { label: string; shortLabel: string } {
  if (trial.phase === TrialPhase.TRIALING) {
    if (trial.daysRemaining <= 0) {
      return { label: 'Last day of trial', shortLabel: 'Last day' }
    }
    return {
      label: `${days(trial.daysRemaining)} left in trial`,
      shortLabel: `${trial.daysRemaining}d left`,
    }
  }
  if (trial.phase === TrialPhase.GRACE) {
    return trial.daysRemaining <= 0
      ? { label: 'Trial ended — activate today', shortLabel: 'Activate' }
      : {
          label: `Trial ended — ${days(trial.daysRemaining)} to activate`,
          shortLabel: `${trial.daysRemaining}d to activate`,
        }
  }
  return options.isAdmin
    ? { label: 'Read-only — add a card', shortLabel: 'Read-only' }
    : { label: 'Workspace is read-only', shortLabel: 'Read-only' }
}

/** "Ask Ada Admin or Sam Owner" — or a generic line without names. */
export function askAnAdminLine(contacts: string[]): string {
  if (contacts.length === 0) return 'Ask an owner or admin of this workspace.'
  if (contacts.length === 1) return `Ask ${contacts[0]}.`
  const head = contacts.slice(0, -1).join(', ')
  return `Ask ${head} or ${contacts[contacts.length - 1]}.`
}

/** Banner copy for grace (urgent) and lock (persistent). Null while trialing. */
export function trialBannerCopy(
  trial: WorkspaceTrialStatus,
  options: { isAdmin: boolean; organizationName: string; graceDate?: string },
): { title: string; body: string; action: string | null } | null {
  if (trial.phase === TrialPhase.TRIALING) return null
  if (trial.phase === TrialPhase.GRACE) {
    const when = options.graceDate ? `on ${options.graceDate}` : 'soon'
    return {
      title: `Your trial has ended. ${options.organizationName} becomes read-only ${when}.`,
      body: options.isAdmin
        ? 'Add a card to keep everyone working. Nothing is lost either way.'
        : `Ask an admin to activate Scrambl. ${askAnAdminLine(trial.activationContacts)}`,
      action: options.isAdmin ? 'Add card' : null,
    }
  }
  return {
    title: `${options.organizationName} is read-only.`,
    body: options.isAdmin
      ? 'Your data is safe and everyone can still view and export it. Add a card to turn editing back on.'
      : `Your data is safe and you can still view and export it. ${askAnAdminLine(trial.activationContacts)}`,
    action: options.isAdmin ? 'Add card to reactivate' : null,
  }
}
